import { useEffect, useState } from "react";
import { t } from "i18next";
import { AnimatePresence, motion } from "framer-motion";
import { PiPersonSimpleWalk } from "react-icons/pi";
import { FaCamera, FaRoute, FaMap } from "react-icons/fa6";
import { VscFeedback } from "react-icons/vsc";
import { ImageDetection } from "proximiio-js-library";
import Feature from "proximiio-js-library/lib/models/feature";
import { Button } from "./ui/button";
import { cn, getSortedPOIs, getWayfindingConfig } from "@/lib/utils";
import maplibregl from "maplibre-gl";
import useMapStore from "@/store/mapStore";
import useFeaturesStore from "@/store/featuresStore";
import { excludedAmenities } from "@/store/data";
import { HiOutlineMap } from "react-icons/hi";

interface PoiDetailsRoutesProps {
  closestParkingFeature: Feature;
  showMore: boolean;
  setShowMore: (value: boolean) => void;
}

function PoiDetailsRoutes({
  closestParkingFeature,
  showMore,
  setShowMore,
}: PoiDetailsRoutesProps) {
  const features = useFeaturesStore((state) => state.features);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const currentLang = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const startFeature = useMapStore((state) => state.startFeature);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const navMode = useMapStore((state) => state.navMode);
  const cityStart = useMapStore((state) => state.cityStart);
  const cityFinish = useMapStore((state) => state.cityFinish);
  const map = useMapStore((state) => state.map);
  const stops = useMapStore((state) => state.stops);
  const cameraRecognitionFeature = useMapStore(
    (state) => state.cameraRecognitionFeature,
  );
  const accessibleRoute = useMapStore((state) => state.accessibleRoute);
  const haveCustomPosition = useMapStore((state) => state.haveCustomPosition);
  const setRouteStart = useMapStore((state) => state.setRouteStart);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setCameraRecognitionFeature = useMapStore(
    (state) => state.setCameraRecognitionFeature,
  );
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setCurrentStep = useMapStore((state) => state.setCurrentStep);
  const setStepAnimated = useMapStore((state) => state.setStepAnimated);
  const setGlobalSearchOpen = useMapStore((state) => state.setGlobalSearchOpen);

  const entranceFeatureId = import.meta.env.VITE_WAYFINDING_ENTRANCE_FEATURE_ID;
  const feedbackEnabled =
    import.meta.env.VITE_WAYFINDING_COLLECT_FEEDBACK === "true";

  const pois = getSortedPOIs({ features, currentLang, floors });

  const [enableGVision, setEnableGVision] = useState(
    import.meta.env.VITE_WAYDINDING_ENABLE_GVISION === "true",
  );
  const entryLongitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LONGITUDE;
  const entryLatitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LATITUDE;

  useEffect(() => {
    if (!enableGVision) {
      return;
    }
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(function (devices) {
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setEnableGVision(videoDevices.length > 0);
      });
    } else {
      setEnableGVision(false);
    }
  }, [setEnableGVision, enableGVision]);

  const requestLocationPermission = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!("geolocation.getCurrentPosition()" in navigator)) {
        console.log("Geolocation not supported");
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  };

  const getRouteHandler = async (from: string) => {
    if (from !== "createRoute" && gpsMode) {
      try {
        const position = await requestLocationPermission();
        const geolocateControl = map
          .getMapboxInstance()
          ._controls.find(
            (control) => control instanceof maplibregl.GeolocateControl,
          );
        if (geolocateControl) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (geolocateControl as any)._watchState = "BACKGROUND"; // reset internal state
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (geolocateControl as any)._geolocateButton.disabled = false; // enable button
        }
        console.log("current position", position);
        // You can store or use position.coords.latitude / position.coords.longitude here
      } catch (error) {
        console.warn("User denied location or an error occurred:", error);
        // You can handle fallback logic here if needed
        // return; // Stop execution if you only want to proceed when user allows location
      }
    }
    console.log("get route");
    setShowMore(false);
    if (from === "parking" && closestParkingFeature?.properties) {
      console.log("from parking");
      setRouteStart(closestParkingFeature);
      return;
    }
    if (from === "entrance") {
      const entranceFeature = features.find((f) => f.id === entranceFeatureId);
      if (entranceFeature) {
        setRouteStart(entranceFeature);
        console.log("from entrance", entranceFeature);
        return;
      }
    }
    if (from === "other") {
      setShowCustomRoutePicker(true);
      console.log("from other");
      return;
    }
    if (from === "kiosk") {
      setRouteStart("kiosk");
      console.log("from kiosk");
      return;
    }
    if (from === "gvision") {
      ImageDetection.init(
        {
          gVisionApiKey: import.meta.env.VITE_WAYFINDING_GVISION_APIKEY,
          pois: pois.filter((item) => {
            if (excludedAmenities.includes(item.properties.amenity))
              return false;
            return true;
          }),
          cameraText: t("gvision-camera-text"),
          tryAgainButtonText: t("try-again"),
          confirmButtonText: t("confirm"),
          noResultsText: t("gvision-no-results"),
          resultsHeadingText: t("gvision-results-header"),
          returnResults: 1,
          dir: currentLang === "ar" ? "rtl" : "ltr",
        },
        (item) => {
          const feature = features.find((f) => f.id === item.id);
          if (Object.keys(map).length > 0 && feature) {
            const pathPointFeature = map.findPathPoint({
              featureId: feature.id,
              displayOnMap: true,
            });
            map.centerToFeature(pathPointFeature.id);
            setCameraRecognitionFeature(pathPointFeature);
          }
        },
      );
    }
    if (from === "startFeature") {
      let start = cameraRecognitionFeature?.id
        ? cameraRecognitionFeature
        : startFeature;
      if (haveCustomPosition) {
        start = map
          .getMapState()
          .dynamicFeatures.features.find(
            (f: Feature) => f.id === "custom-position",
          );
      }
      setRouteStart(start);
      setShowCustomRoutePicker(false);
      console.log("from", start);
      return;
    }
    if (from === "multipoint") {
      const start = stops[0].feature;
      if (start) {
        setRouteStart(start);
      }
      return;
    }
    if (from === "createRoute") {
      setRouteFinish({} as Feature);
      setGlobalSearchOpen(true);
      return;
    }
  };

  const openGoogleMaps = () => {
    // Create the Google Maps navigation URL
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${cityStart.coordinates[1]},${cityStart.coordinates[0]}&destination=${cityFinish.coordinates[1]},${cityFinish.coordinates[0]}&travelmode=driving`;

    // Open the Google Maps navigation URL in a new tab
    window.open(googleMapsUrl, "_blank");
  };

  const runCityNav = () => {
    if (Object.keys(map).length > 0) {
      if (
        cityStart?.coordinates?.length > 0 &&
        cityFinish?.coordinates?.length > 0
      ) {
        const combinedRoute = cityStart.poiId || cityFinish.poiId;
        const startAtMall = cityStart.poiId;

        if (combinedRoute) {
          map.findCombinedRoute({
            start: startAtMall
              ? cityStart.poiId!
              : {
                  lng: cityStart.coordinates[0],
                  lat: cityStart.coordinates[1],
                },
            connectingPoint: {
              lng: entryLongitude,
              lat: entryLatitude,
            },
            destination: !startAtMall
              ? cityFinish.poiId!
              : {
                  lng: cityFinish.coordinates[0],
                  lat: cityFinish.coordinates[1],
                },
            accessibleRoute: accessibleRoute,
            wayfindingConfig: getWayfindingConfig(accessibleRoute),
          });
        } else {
          map.findCityRoute({
            start: {
              lng: cityStart.coordinates[0],
              lat: cityStart.coordinates[1],
            },
            destination: {
              lng: cityFinish.coordinates[0],
              lat: cityFinish.coordinates[1],
            },
            autoStart: true,
          });
        }

        setCurrentStep(0);
        setTimeout(() => {
          setStepAnimated(0);
        }, 2000);
      }
    }
  };

  return (
    <AnimatePresence>
      {navMode !== "mall" && (
        <motion.div
          className={cn(
            "mt-4 flex flex-col gap-2",
            haveRouteDetails && "lg:block",
            haveRouteDetails && !showMore && "hidden",
          )}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.2,
          }}
        >
          <Button
            onClick={runCityNav}
            className="flex h-auto w-full gap-2 rounded-full lg:py-3"
          >
            <FaRoute className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
            {t("takeMeThere")}
          </Button>
          <Button
            onClick={openGoogleMaps}
            className="flex h-auto w-full gap-2 rounded-full border-primary text-primary hover:border-primary/60 hover:text-primary/60 lg:py-3"
            variant={"outline"}
          >
            <FaMap className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
            {t("use-google-maps")}
          </Button>
        </motion.div>
      )}
      {(kioskMode || gpsMode) && navMode === "mall" && (
        <motion.div
          className={cn(
            "mt-4",
            haveRouteDetails && "lg:block",
            haveRouteDetails && !showMore && "hidden",
          )}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.2,
          }}
        >
          <Button
            onClick={() =>
              getRouteHandler(stops.length > 1 ? "multipoint" : "kiosk")
            }
            className="flex w-full rounded-full"
          >
            <PiPersonSimpleWalk className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
            {t("takeMeThere")}
          </Button>
          {mobileMode && (
            <Button
              onClick={() => getRouteHandler("createRoute")}
              className="mt-2 flex w-full rounded-full"
            >
              <HiOutlineMap className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
              {t("create-route")}
            </Button>
          )}
        </motion.div>
      )}
      {!kioskMode && !gpsMode && navMode === "mall" && (
        <motion.div
          className={cn(
            "mt-3 grid gap-3 lg:mt-4",
            haveRouteDetails && !showMore && "hidden",
            enableGVision && "gap-3",
          )}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.2,
          }}
        >
          {startFeature.id ||
          stops.length > 1 ||
          cameraRecognitionFeature?.id ||
          haveCustomPosition ? (
            <Button
              onClick={() =>
                getRouteHandler(
                  stops.length > 1 ? "multipoint" : "startFeature",
                )
              }
              className="flex h-auto w-full gap-2 whitespace-normal rounded-full py-3 font-light"
            >
              <PiPersonSimpleWalk className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
              {t("takeMeThere")}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => getRouteHandler("other")}
                className="flex h-auto gap-2 whitespace-normal rounded-full py-3 font-light"
              >
                <FaRoute className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
                {t("choose-start")}
              </Button>
              {mobileMode && enableGVision && (
                <Button
                  onClick={() => getRouteHandler("gvision")}
                  className="flex h-auto gap-2 whitespace-normal rounded-full py-3 font-light"
                >
                  <FaCamera className="text-2xl group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
                  {t("locate-start")}
                </Button>
              )}
              {feedbackEnabled && (!mobileMode || showMore) && (
                <p
                  className={cn(
                    "flex cursor-pointer items-center justify-center gap-3 text-sm font-bold text-primary hover:text-primary/70 lg:py-3",
                  )}
                  onClick={() => {
                    setOverlayContent("feedback");
                  }}
                >
                  <span className={cn("text-2xl")}>
                    <VscFeedback />
                  </span>
                  {t("send-us-feedback")}
                </p>
              )}
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PoiDetailsRoutes;
